import React, {useEffect} from 'react';
import {Box} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
// import ReactJson from 'react-json-view';
import {JsonViewer} from '@textea/json-viewer';
import {useAccountContext} from '../../../contexts/AccountContext';
import Loading from '../../../components/Loading/Loading';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {BuilderEditorForm} from './CreateBuilderHelpers';
import {
  CreateBuilderProps,
  CreateBuilderProvider,
  useBuilderContext,
} from './useBuilderContext';
import useTitle from '../../../hooks/useTitle';
import {ErrorPage} from '../../../pages/ErrorPage';
import {mainPlanTypeType, builderFieldsType} from './builderFields';
import {builderValidation} from './builderValidation';
import OWNERSHIP from '../../../constants/Ownership';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {BUILDER_TYPES, SPP_BUILDER_TYPES} from '../constants/SolutionType';
import {getCuroURL} from '../../../utils/getCuroURL';
import {useDjangoContext} from '../../../contexts/useAuthContext';
import RecommendsTable from '../../../components/RecommendsTable/RecommendsTable';
import saltus from '../../../shared/logger';
import {SaltusAny} from '../../../utils/SaltusAny';
import {getBuilderInitialValues} from './getBuilderInitialValues';

export const CreateBuilderInner: React.FC = () => {
  const {account, accountError, accountLoading} = useAccountContext();
  const {djangoUser} = useDjangoContext();
  const {reviewDetails} = useReviewContext();
  const {isSPPDA: isSppDa, loadingOvRecommend, builderDetails} = useBuilderContext();
  const [searchParams, setSearchParams] = useSearchParams();
  useTitle(`New requirement - ${account?.name}`);
  useEffect(() => {
    if (builderDetails) {
      saltus.log({'Loaded recommend values': builderDetails});
    }
  }, [builderDetails]);

  if (accountError) {
    return (
      <ErrorPage
        // retry={accountsRetry}
        code="500"
        text="We were unable to retrieve the account information."
      />
    );
  }

  const ovrecommendId = searchParams.get('ovrecommendId');
  if (accountLoading || loadingOvRecommend || (ovrecommendId && !builderDetails)) {
    return (
      <Box style={{marginTop: '10rem'}}>
        <Loading entityName="account" />
      </Box>
    );
  }

  if (!account) {
    return <ErrorPage code="404" text="Account not found" />;
  }

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  const initialValueOverrides: Partial<builderFieldsType> = {
    note: builderDetails?.note || undefined,
    how: 'Recommendation',
    contact: '782d5c26-768a-4be9-ab57-ae4fbfcc535a', // jon
    // ownership: OWNERSHIP.SINGLE_LIFE,
    planAction: OVRecommendActionEnum.SET_UP_NEW,
    mainPlanType: 'gia',
    goal: {
      __typename: 'GoalType',
      id: '9dbbb584-cc5e-4727-bbf6-836141697e43',
      name: 'Buy the children a flat',
      ownership: 'Account owned',
      contact: null,
    },
  };
  const debugServerValue = JSON.stringify(builderDetails || null, null, 2);

  return (
    <BuilderEditorForm
      builderAccount={account}
      debugChildrenValue={debugServerValue}
      debugChildren={
        <>
          <RecommendsTable recommends={builderDetails?.recommends || []} />
          {/* <ReactJson
            src={builderDetails || {}}
            name={false}
            shouldCollapse={(x) => {
              if (x.name && ['goal', 'review', 'contact', 'account'].includes(x.name)) {
                return true;
              }
              return false;
            }}
            collapsed={process.env.NODE_ENV === 'development' ? 3 : 1}
          /> */}
          <JsonViewer
            value={builderDetails || {}}
            editable={false}
            defaultInspectControl={(path: SaltusAny, val: SaltusAny) => {
              if (
                ['goals', 'review'].some((item) => {
                  return item === path[0];
                })
              ) {
                return false;
              }
              return true;
            }}
            // theme={theme}
            // indentWidth={indent}
            // highlightUpdates={true}
            // onChange={handleChange}
            // keyRenderer={KeyRenderer}
            // valueTypes={[imageType]}
          />
          {builderDetails?.recommends?.map((r) => (
            <a
              key={r.curoId}
              href={getCuroURL(djangoUser, 'recommendation', String(r.curoId))}
            >
              {r.curoId}: Failed: {r.isFailedSubmission}
            </a>
          ))}
          <div className="recommends-list">
            {builderDetails?.recommends?.map((x) => {
              const text = `'${x.advice}',`;
              return (
                <div key={x.id} className="recommend">
                  {text}
                </div>
              );
            })}
          </div>
        </>
      }
      initialValueOverrides={
        process.env.NODE === 'development'
          ? initialValueOverrides
          : getBuilderInitialValues({
              builderDetails,
              reviewDetails,
              searchParams,
              isSppDa: isSppDa || false,
            })
      }
      submit={() => {}}
      formFields={[]}
      validate={(values) => {
        const errors = builderValidation(values);
        return errors;
      }}
    />
  );
};

export function CreateBuilder(props: CreateBuilderProps) {
  return (
    <CreateBuilderProvider {...props}>
      <CreateBuilderInner />
    </CreateBuilderProvider>
  );
}
